import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { validate as isUuid } from 'uuid';
import { Accordion, Alert, Badge, Button, Card, Form, InputGroup, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import {
  ADMIN_BLOCK_USER_FRIEND_RELATION,
  ADMIN_FORCE_ADD_FRIEND_RELATION,
  ADMIN_UNBLOCK_USER_FRIEND_RELATION,
  ADMIN_USER_FRIEND_RELATIONS,
} from '../../../graphql';

type FriendRelation = {
  id: string;
  fromId: string;
  toId: string;
  rawState: string;
  stateBeforeBlock?: string | null;
  displayStatus: string;
  opponent: {
    id: string;
    displayName: string;
    picture?: string | null;
  };
};

type FriendRelationsPayload = {
  id: string;
  hasMore: boolean;
  totalCount: number;
  list: FriendRelation[];
};

type FriendRelationsQuery = {
  adminUserFriendRelations: FriendRelationsPayload;
};

type FriendRelationsQueryVariables = {
  input: {
    userId: string;
    opponentUserId?: string;
    opponentName?: string;
    limit?: number;
    offset?: number;
  };
};

type AdminUnblockUserFriendRelationMutation = {
  adminUnblockUserFriendRelation: {
    success: boolean;
  };
};

type AdminUnblockUserFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type AdminBlockUserFriendRelationMutation = {
  adminBlockUserFriendRelation: {
    success: boolean;
  };
};

type AdminBlockUserFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type AdminForceAddFriendRelationMutation = {
  adminForceAddFriendRelation: {
    success: boolean;
  };
};

type AdminForceAddFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type Props = {
  userId: string;
  userName?: string | null;
};

const PAGE_SIZE = 10;

const getBadgeVariant = (displayStatus: string) => {
  if (displayStatus === 'Blocked') {
    return 'danger';
  }
  if (displayStatus.startsWith('Pending')) {
    return 'warning';
  }
  if (displayStatus === 'Normal') {
    return 'success';
  }
  return 'secondary';
};

type SearchType = 'id' | 'name';

type ActiveSearch = {
  type: SearchType;
  value: string;
};

type RelationGroupKey = 'friend' | 'pending' | 'blockedByUser' | 'blockedByPlayer' | 'neutral';

const RELATION_GROUPS: { key: RelationGroupKey; label: string }[] = [
  { key: 'friend', label: 'Friends' },
  { key: 'pending', label: 'Pending Requests' },
  { key: 'blockedByUser', label: 'Blocked by User' },
  { key: 'blockedByPlayer', label: 'Blocked This User' },
  { key: 'neutral', label: 'No Relation' },
];

// BLOCKED_FROM: fromId blocked toId. BLOCKED_TO: toId blocked fromId. BLOCKED: both did.
const getBlockers = (relation: FriendRelation, currentUserId: string) => {
  switch (relation.rawState) {
    case 'BLOCKED':
      return { byUser: true, byPlayer: true };
    case 'BLOCKED_FROM':
      return { byUser: relation.fromId === currentUserId, byPlayer: relation.fromId !== currentUserId };
    case 'BLOCKED_TO':
      return { byUser: relation.toId === currentUserId, byPlayer: relation.toId !== currentUserId };
    default:
      return { byUser: false, byPlayer: false };
  }
};

const isBlockedState = (rawState: string) => ['BLOCKED', 'BLOCKED_FROM', 'BLOCKED_TO'].includes(rawState);

// Matches the backend: lifting the last block restores a friendship, anything else goes back to NEUTRAL.
// stateBeforeBlock is null for blocks made before it was tracked.
const getStateAfterUnblock = (relation: FriendRelation) => (relation.stateBeforeBlock === 'FRIEND' ? 'FRIEND' : 'NEUTRAL');

const getStateBeforeBlockLabel = (relation: FriendRelation) => {
  if (!isBlockedState(relation.rawState)) {
    return '-';
  }
  return relation.stateBeforeBlock ?? 'Unknown (blocked before tracking)';
};

// A mutual block shows up in both blocked tabs
const getRelationGroups = (relation: FriendRelation, currentUserId: string): RelationGroupKey[] => {
  if (relation.rawState === 'FRIEND') {
    return ['friend'];
  }
  if (relation.rawState === 'PENDING') {
    return ['pending'];
  }
  const { byUser, byPlayer } = getBlockers(relation, currentUserId);
  if (byUser || byPlayer) {
    return [...(byUser ? ['blockedByUser' as const] : []), ...(byPlayer ? ['blockedByPlayer' as const] : [])];
  }
  return ['neutral'];
};

const RelationName = ({ name, fallback }: { name?: string | null; fallback: string }) =>
  name ? <b>{name}</b> : <>{fallback}</>;

// Spelled out with both names so the admin never has to work out who "you" is
const getRelationDetails = (relation: FriendRelation, currentUserId: string, userName?: string | null) => {
  const user = <RelationName name={userName} fallback="This user" />;
  const player = <RelationName name={relation.opponent.displayName} fallback="the player" />;

  switch (relation.rawState) {
    case 'BLOCKED':
    case 'BLOCKED_FROM':
    case 'BLOCKED_TO': {
      const { byUser, byPlayer } = getBlockers(relation, currentUserId);
      if (byUser && byPlayer) {
        return (
          <>
            {user} and {player} blocked each other
          </>
        );
      }
      return byUser ? (
        <>
          {user} blocked {player}
        </>
      ) : (
        <>
          {player} blocked {user}
        </>
      );
    }
    case 'PENDING':
      return relation.fromId === currentUserId ? (
        <>
          {user} sent a friend request to {player}
        </>
      ) : (
        <>
          {player} sent a friend request to {user}
        </>
      );
    case 'FRIEND':
      return (
        <>
          {user} and {player} are friends
        </>
      );
    case 'NEUTRAL':
    default:
      return '-';
  }
};

export default function FriendListDisplay({ userId, userName }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState<ActiveSearch | null>(null);
  const [relations, setRelations] = useState<FriendRelation[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionRelationId, setActionRelationId] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<RelationGroupKey>('friend');

  const [fetchRelations, { loading, error }] = useLazyQuery<FriendRelationsQuery, FriendRelationsQueryVariables>(
    ADMIN_USER_FRIEND_RELATIONS,
    { fetchPolicy: 'network-only' }
  );
  const [unblockRelation] = useMutation<
    AdminUnblockUserFriendRelationMutation,
    AdminUnblockUserFriendRelationMutationVariables
  >(ADMIN_UNBLOCK_USER_FRIEND_RELATION);
  const [blockRelation] = useMutation<
    AdminBlockUserFriendRelationMutation,
    AdminBlockUserFriendRelationMutationVariables
  >(ADMIN_BLOCK_USER_FRIEND_RELATION);
  const [forceAddFriendRelation] = useMutation<
    AdminForceAddFriendRelationMutation,
    AdminForceAddFriendRelationMutationVariables
  >(ADMIN_FORCE_ADD_FRIEND_RELATION);

  const loadRelations = useCallback(
    async ({
      search = null,
      offset = 0,
      append = false,
    }: {
      search?: ActiveSearch | null;
      offset?: number;
      append?: boolean;
    }) => {
      if (!append) {
        setHasLoadedInitial(true);
      }

      const isIdSearch = search?.type === 'id';
      const { data } = await fetchRelations({
        variables: {
          input: {
            userId,
            opponentUserId: isIdSearch ? search?.value : undefined,
            opponentName: search?.type === 'name' ? search.value : undefined,
            limit: isIdSearch ? 1 : PAGE_SIZE,
            offset: isIdSearch ? 0 : offset,
          },
        },
      });

      setActiveSearch(search);
      const payload = data?.adminUserFriendRelations;
      if (!payload) {
        setRelations([]);
        setHasMore(false);
        return;
      }

      setRelations((current) => (append ? [...current, ...payload.list] : payload.list));
      setHasMore(payload.hasMore);
      if (search && !append && payload.list.length > 0) {
        setActiveGroup(getRelationGroups(payload.list[0], userId)[0]);
      }
    },
    [fetchRelations, userId]
  );

  useEffect(() => {
    setRelations([]);
    setHasMore(false);
    setActiveSearch(null);
    setSearchValue('');
    setHasLoadedInitial(false);
    setActionError(null);
    setActionRelationId(null);
    setActiveGroup('friend');
  }, [userId]);

  useEffect(() => {
    if (expanded && !hasLoadedInitial && !loading) {
      loadRelations({}).catch(() => undefined);
    }
  }, [expanded, hasLoadedInitial, loadRelations, loading]);

  const handleSearch = async () => {
    const value = searchValue.trim();
    // Same rule as the recovery search: a UUID is a user ID, anything else is a name
    await loadRelations({ search: value ? { type: isUuid(value) ? 'id' : 'name', value } : null });
  };

  const handleReset = async () => {
    setSearchValue('');
    await loadRelations({});
  };

  const handleLoadMore = async () => {
    await loadRelations({ search: activeSearch, offset: relations.length, append: true });
  };

  const handleSearchClick = () => {
    handleSearch().catch(() => undefined);
  };

  const handleResetClick = () => {
    handleReset().catch(() => undefined);
  };

  const handleLoadMoreClick = () => {
    handleLoadMore().catch(() => undefined);
  };

  const handleUnblockClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await unblockRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      await loadRelations({ search: activeSearch });
    } catch {
      setActionError('Failed to unblock relation');
    } finally {
      setActionRelationId(null);
    }
  };

  const handleBlockClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await blockRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      await loadRelations({ search: activeSearch });
    } catch {
      setActionError('Failed to block relation');
    } finally {
      setActionRelationId(null);
    }
  };

  const handleForceAddFriendClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await forceAddFriendRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      await loadRelations({ search: activeSearch });
    } catch {
      setActionError('Failed to force add friend relation');
    } finally {
      setActionRelationId(null);
    }
  };

  const renderActions = (relation: FriendRelation) => {
    const isUpdating = actionRelationId === relation.id;

    if (relation.displayStatus === 'Blocked') {
      return (
        <Button
          size="sm"
          variant="success"
          disabled={isUpdating}
          onClick={() => {
            handleUnblockClick(relation).catch(() => undefined);
          }}
        >
          {isUpdating ? 'Updating...' : `Unblock → ${getStateAfterUnblock(relation) === 'FRIEND' ? 'Friend' : 'Neutral'}`}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center">
        <Button
          size="sm"
          variant="danger"
          disabled={isUpdating}
          onClick={() => {
            handleBlockClick(relation).catch(() => undefined);
          }}
        >
          {isUpdating ? 'Updating...' : 'Block'}
        </Button>
        {relation.rawState === 'FRIEND' ? (
          <Button
            size="sm"
            variant="secondary"
            className="ml-2"
            disabled={isUpdating}
            onClick={() => {
              handleUnblockClick(relation).catch(() => undefined);
            }}
          >
            {isUpdating ? 'Updating...' : 'Remove Friend'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="primary"
            className="ml-2"
            disabled={isUpdating}
            onClick={() => {
              handleForceAddFriendClick(relation).catch(() => undefined);
            }}
          >
            {isUpdating ? 'Updating...' : 'Force Add Friend'}
          </Button>
        )}
      </div>
    );
  };

  // Long names are cut in the tab; the full wording stays in the tooltip
  const shortUserName = userName && userName.length > 16 ? `${userName.slice(0, 15)}…` : userName;
  const getGroupTitle = (key: RelationGroupKey, label: string) => {
    if (key === 'blockedByUser') {
      return {
        label: shortUserName ? `Players ${shortUserName} Blocked` : 'Players This User Blocked',
        hint: `Players that ${userName || 'this user'} has blocked`,
      };
    }
    if (key === 'blockedByPlayer') {
      return {
        label: shortUserName ? `Players Who Blocked ${shortUserName}` : 'Players Who Blocked This User',
        hint: `Players that have blocked ${userName || 'this user'}`,
      };
    }
    return { label, hint: label };
  };

  const groupedRelations = RELATION_GROUPS.map((group) => ({
    ...group,
    ...getGroupTitle(group.key, group.label),
    list: relations.filter((relation) => getRelationGroups(relation, userId).includes(group.key)),
  }));

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded((value) => !value)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Player Relations</span>
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <div>
              <p className="text-muted small mb-2">
                <i>
                  Review and manage this user&apos;s relations with other players (friends, pending
                  requests, blocks, and other players they&apos;ve interacted with), or look up a specific
                  player by user ID.
                </i>
              </p>

              <div className="mb-3 p-3 uv-quick-lookup-panel" style={{ borderRadius: 14 }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                  <div>
                    <div className="uv-quick-lookup-title" style={{ fontSize: 13, fontWeight: 600 }}>
                      Quick Lookup
                    </div>
                    <div className="text-muted small">
                      Find this user&apos;s relation with a specific player by user ID or name without scanning
                      the full list.
                    </div>
                  </div>
                  <div
                    className="uv-quick-lookup-count"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 999,
                      padding: '6px 10px',
                    }}
                  >
                    {loading ? 'Loading...' : `${relations.length} rows loaded`}
                  </div>
                </div>

                <InputGroup size="sm" style={{ maxWidth: 560 }}>
                  <Form.Control
                    placeholder="Enter player user ID or name"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSearchClick();
                      }
                    }}
                    style={{
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                      minHeight: 42,
                    }}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearchClick}
                    disabled={loading}
                    style={{ minWidth: 96, fontWeight: 600, marginRight: 8 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="light"
                    className="uv-quick-lookup-reset"
                    onClick={handleResetClick}
                    disabled={loading}
                    style={{ minWidth: 88, fontWeight: 600 }}
                  >
                    Reset
                  </Button>
                </InputGroup>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                  {activeSearch?.type === 'id' && 'Showing the relation with the searched player ID.'}
                  {activeSearch?.type === 'name' && `Showing relations with players whose name contains "${activeSearch.value}".`}
                  {!activeSearch && 'Showing loaded relations grouped by status. Load more to include older relations.'}
                </div>
              </div>

              {error && (
                <Alert variant="danger" className="py-1 mb-2">
                  Failed to load player relations
                </Alert>
              )}
              {actionError && (
                <Alert variant="danger" className="py-1 mb-2">
                  {actionError}
                </Alert>
              )}

              {loading && !relations.length ? (
                <div className="py-3 text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : relations.length > 0 ? (
                <>
                  <Tabs
                    activeKey={activeGroup}
                    onSelect={(key) => key && setActiveGroup(key as RelationGroupKey)}
                    className="uv-relation-tabs mb-3"
                  >
                    {groupedRelations.map((group) => (
                      <Tab
                        key={group.key}
                        eventKey={group.key}
                        tabClassName={`uv-relation-tab uv-relation-tab--${group.key}`}
                        title={
                          <span className="d-flex align-items-center" style={{ gap: 6 }} title={group.hint}>
                            <span className="uv-relation-tab-dot" />
                            {group.label}
                            <span className="uv-relation-tab-count">{group.list.length}</span>
                          </span>
                        }
                      >
                        {group.list.length > 0 ? (
                          <div className="table-responsive">
                            <Table bordered hover size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  <th className="text-center">Player User ID</th>
                                  <th className="text-center">Player Name</th>
                                  <th className="text-center">Status</th>
                                  <th className="text-center">Raw State</th>
                                  <th className="text-center">State Before Block</th>
                                  <th className="text-center">Details</th>
                                  <th className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.list.map((relation) => (
                                  <tr key={relation.id}>
                                    <td className="text-center">{relation.opponent.id}</td>
                                    <td className="text-center">{relation.opponent.displayName}</td>
                                    <td className="text-center">
                                      <Badge variant={getBadgeVariant(relation.displayStatus)}>
                                        {relation.displayStatus}
                                      </Badge>
                                    </td>
                                    <td className="text-center">{relation.rawState}</td>
                                    <td className="text-center">{getStateBeforeBlockLabel(relation)}</td>
                                    <td className="text-center">{getRelationDetails(relation, userId, userName)}</td>
                                    <td className="text-center">{renderActions(relation)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted small mb-0 py-2">
                            <i>
                              {activeSearch
                                ? 'No searched player in this status.'
                                : `Nothing here yet: no ${group.hint.toLowerCase()} in the loaded relations.`}
                            </i>
                          </p>
                        )}
                      </Tab>
                    ))}
                  </Tabs>

                  {activeSearch?.type !== 'id' && hasMore && (
                    <div className="d-flex justify-content-center mt-3">
                      <Button variant="outline-primary" onClick={handleLoadMoreClick} disabled={loading}>
                        {loading ? 'Loading...' : 'Load More'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted mb-0">
                  <i>{activeSearch?.type === 'id'
                      ? 'No relation found for that player user ID.'
                      : activeSearch?.type === 'name'
                        ? 'No relation found for a player with that name.'
                        : 'No player relations found.'}</i>
                </p>
              )}
            </div>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
